<script setup>
import { isEmpty, isNumber } from 'lodash'
import { computed, inject, onBeforeUnmount, onMounted, provide, watch } from 'vue'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import {
  CUSTOM_DASHBOARD_MODULE_ATTRIBUTES,
  MODULE_TYPE_IDS,
  normalizeDashboardModule,
  CUSTOM_DASHBOARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT,
  CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS
} from '@/utils/custom-dashboard-helper'
import { usePeriodGranulation, useViewPointLastDate } from '@/utils/element-progress'
import { ELEMENT_PROGRESS_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import {
  currentModuleStateInjectionKey,
  customDashboardModulesStateInjectionKey,
  customDashboardStateInjectionKey
} from '@/utils/injection-keys'

import CustomDashboardModuleItem from '@/components/dashboard/custom-dashboard/CustomDashboardModuleItem'
import CustomDashboardProgressModuleContent from '@/components/dashboard/custom-dashboard/modules/CustomDashboardProgressModuleContent'
import ModuleDropdownMenu from '@/components/dashboard/custom-dashboard/modules/moduleActions/ModuleDropdownMenu'
import ProgressViewColumnsSelect from '@/components/objectives/progress-view/ProgressViewColumnsSelect'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDatesNavigation from '@/components/ui/AppDatesNavigation/AppDatesNavigation'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'

defineOptions({
  name: 'CustomDashboardProgressModule'
})

const props = defineProps({
  formModel: {
    type: Object,
    default: () => ({})
  },

  dashboardItem: {
    type: Object,
    default: () => ({})
  }
})

const modulesState = inject(customDashboardModulesStateInjectionKey)

const moduleId = computed(() => props.formModel.id)

const currentModuleState = computed(() => {
  return modulesState.value[moduleId.value] || {}
})

provide(currentModuleStateInjectionKey, currentModuleState)

const getProgressData = async ({
  resetToInitialState = false,
  externalViewPointLastDate = viewPointLastDate.value,
  externalGranulationId = currentModuleState.value.currentGranulationId,
  resetViewPointLastDate = false
} = {}) => {
  modulesState.value.toggleModuleLoading(moduleId.value)

  const customDashboardApi = new CustomDashboardApiHandler()
  try {
    const module = await customDashboardApi.getProgressModuleData({
      moduleId: props.formModel.id,
      viewPointLastDate: resetToInitialState ? undefined : externalViewPointLastDate,
      id: props.dashboardItem.id,
      granulationId: externalGranulationId,
      columnCount: currentModuleState.value.columnCount
    })

    if (resetViewPointLastDate) {
      resetInitialViewPoint()
    }

    customDashboardState.value.updateModuleData({
      ...normalizeDashboardModule({
        module,
        moduleParams: [
          {
            moduleId: moduleId.value,
            granulationId: externalGranulationId
          }
        ]
      })
    })
  } catch (error) {
    handleError({ error })
  } finally {
    modulesState.value.toggleModuleLoading(moduleId.value)
  }
}

watch(
  () => props.formModel[CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID.attributeDefaultValueKey],
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && oldValue) {
      resetInitialViewPoint()
    }
  },
  { immediate: true }
)

watch(
  () => props.formModel[CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID.attributeKey],
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && oldValue) {
      modulesState.value.setModuleGranulationId(moduleId.value, newValue)
    }
  },
  { immediate: true }
)

watch(
  () => props.formModel[CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.COLUMN_COUNT.attributeKey],
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      modulesState.value.setColumnCount(moduleId.value, newValue)
    }
  },
  { immediate: true }
)

const {
  viewPointLastDate,
  isResetButtonDisabled,
  resetInitialViewPoint,
  initialViewPointLastDate,
  viewPointLastDatesForNavigation
} = useViewPointLastDate({
  [ELEMENT_PROGRESS_ENTITY_KEYS.PERIODS]: () => props.formModel.periods || [],
  shouldStopWatcher: false,
  currentGranulationId: () => currentModuleState.value.currentGranulationId
})

const { getPeriodGranulationOptions } = usePeriodGranulation({ pluralizeOptions: true })

const isNoElements = computed(() => {
  return isEmpty(props.formModel.elements)
})

const customDashboardState = inject(customDashboardStateInjectionKey)

const isReadMode = computed(() => Boolean(customDashboardState.value.isReadMode))

const moveForward = () => {
  getProgressData({
    externalViewPointLastDate: viewPointLastDatesForNavigation.value.forward
  })
}

const moveBackward = () => {
  getProgressData({
    externalViewPointLastDate: viewPointLastDatesForNavigation.value.backward
  })
}

const moveToStartpoint = () => {
  getProgressData({
    resetToInitialState: true
  })
}

const onGranulationChange = async externalGranulationId => {
  await getProgressData({
    resetToInitialState: true,
    externalGranulationId,
    resetViewPointLastDate: true
  })
  modulesState.value.setModuleGranulationId(moduleId.value, externalGranulationId)
}

const onUpdateColumnCount = count => {
  modulesState.value.setColumnCount(moduleId.value, count)
  getProgressData()
}

onMounted(() => {
  modulesState.value.initModuleState({
    moduleId: moduleId.value,
    state: {
      typeId: props.formModel.typeId || MODULE_TYPE_IDS.KPI,
      isLoading: false,
      currentGranulationId: props.formModel.granulationId,
      viewPointLastDate,
      initialViewPointLastDate,
      columnCount:
        isNumber(props.formModel.columnCount) &&
        props.formModel.columnCount > 0 &&
        CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS.includes(props.formModel.columnCount)
          ? props.formModel.columnCount
          : CUSTOM_DASHBOARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT
    }
  })
})

onBeforeUnmount(() => {
  modulesState.value.destroyModuleState(moduleId.value)
})
</script>

<template>
  <CustomDashboardModuleItem style="--content-padding: 0">
    <template #title> {{ formModel.name }}</template>

    <template #actions>
      <div class="cdpm-Actions" data-export-ignore>
        <template v-if="!isNoElements">
          <ProgressViewColumnsSelect
            v-if="!isEmpty(currentModuleState)"
            class="cdpm-Actions_ColumnsSelect"
            :disabled="currentModuleState.isLoading"
            :count="currentModuleState.columnCount"
            :options="CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS"
            @update-count="onUpdateColumnCount"
          />
          <AppDatesNavigation
            :disabled="currentModuleState.isLoading"
            :is-reset-disabled="isResetButtonDisabled"
            @move-to-startpoint="moveToStartpoint"
            @move-backward="moveBackward"
            @move-forward="moveForward"
          />
          <AppRadioGroup
            v-if="!isEmpty(currentModuleState)"
            :disabled="currentModuleState.isLoading"
            :model-value="currentModuleState.currentGranulationId"
            :name="`module-view-switch-${formModel.id}`"
            :options="getPeriodGranulationOptions"
            @update:model-value="onGranulationChange"
          />
        </template>

        <ModuleDropdownMenu
          v-if="customDashboardState.userCanManageCustomDashboard && !isReadMode"
          :form-model="formModel"
        />
      </div>
    </template>

    <template #content>
      <SettingsPagesEmptyState
        v-if="isNoElements"
        hero-height="128"
        hero-icon="no-groups-hero"
        hero-width="142"
      >
        <template #title> {{ $t('dashboard.no_elements_yet') }} </template>
        <template #subtitle> {{ $t('dashboard.you_dont_have_elements') }} </template>

        <template v-if="customDashboardState.userCanManageCustomDashboard && !isReadMode" #action>
          <AppButton
            height="24"
            icon="plus-next"
            type="primary-next"
            width="24"
            @click="customDashboardState.addElementsToModule(formModel)"
          >
            {{ $t('create.elements.btn.add') }}
          </AppButton>
        </template>
      </SettingsPagesEmptyState>

      <CustomDashboardProgressModuleContent v-else :module-data="formModel" />
    </template>
  </CustomDashboardModuleItem>
</template>

<style lang="scss" scoped>
.cdpm-Actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cdpm-Actions_ColumnsSelect {
  --pvc-trigger-background: #{$grey-3-next};
  --pvc-counter-size: 32px;
  --pvc-counter-padding-inline: 8px 5px;
}
</style>
